import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, TrendingUp, TrendingDown, Minus, ExternalLink, MapPin } from 'lucide-react';
import { BusinessDetails } from '@/types/index';
import { cn } from '@/lib/core/utils';
import { useGooglePlacesData } from '@/hooks/business/useGooglePlacesData';

interface GoogleReviewCardProps {
  business: BusinessDetails;
  className?: string;
  showGrowthMetrics?: boolean;
  compact?: boolean;
}

export const GoogleReviewCard: React.FC<GoogleReviewCardProps> = ({
  business,
  className,
  showGrowthMetrics = true,
  compact = false
}) => {
  const { data: googleData, loading, error, refetch } = useGooglePlacesData(business.google_place_id);

  // Calculate growth metrics
  const getGrowthMetrics = () => {
    if (!googleData || !business.initial_google_rating || !business.initial_google_review_count) {
      return null;
    }

    const ratingChange = googleData.rating - business.initial_google_rating;
    const reviewsGrowth = googleData.userRatingCount - business.initial_google_review_count;

    return {
      ratingChange,
      reviewsGrowth,
      ratingTrend: ratingChange > 0.1 ? 'up' : ratingChange < -0.1 ? 'down' : 'neutral'
    };
  };

  const growthMetrics = getGrowthMetrics();

  // Render star rating
  const renderStars = (rating: number, size: 'sm' | 'md' = 'md') => {
    const starSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={cn(
              starSize,
              i < fullStars
                ? 'fill-yellow-400 text-yellow-400'
                : i === fullStars && hasHalfStar
                ? 'fill-yellow-200 text-yellow-400'
                : 'text-gray-300'
            )}
          />
        ))}
        <span className={`ml-1 font-medium ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  // Don't render if no Google Place ID
  if (!business.google_place_id) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Google Places integration not configured</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className={cn("flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0", compact && "pb-2")}>
        <CardTitle className="text-sm font-medium">Google Reviews</CardTitle>
        {!compact && (
          <CardDescription>Current data from Google Business Profile</CardDescription>
        )}
      </CardHeader>

      <CardContent className="h-64 flex flex-col justify-center">
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="text-center">
            <p className="text-sm text-destructive mb-2">{error}</p>
            <button
              onClick={refetch}
              className="text-xs text-primary hover:text-primary/80 underline"
            >
              Retry
            </button>
          </div>
        ) : googleData ? (
          <div className="space-y-4">
            {/* Current Rating & Reviews */}
            <div className="text-center">
              {renderStars(googleData.rating)}
              <p className="text-2xl font-bold mt-1">
                {googleData.userRatingCount.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Google Reviews</p>
            </div>

            {/* Growth Metrics */}
            {showGrowthMetrics && growthMetrics && (
              <div className="flex justify-center space-x-6 text-center">
                <div>
                  <div className="flex items-center justify-center space-x-1">
                    {growthMetrics.ratingTrend === 'up' ? (
                      <TrendingUp className="w-3 h-3 text-green-600" />
                    ) : growthMetrics.ratingTrend === 'down' ? (
                      <TrendingDown className="w-3 h-3 text-red-600" />
                    ) : (
                      <Minus className="w-3 h-3 text-muted-foreground" />
                    )}
                    <span className={cn(
                      "text-sm font-medium",
                      growthMetrics.ratingChange > 0.1 ? "text-green-600" :
                      growthMetrics.ratingChange < -0.1 ? "text-red-600" : "text-muted-foreground"
                    )}>
                      {growthMetrics.ratingChange >= 0 ? '+' : ''}{growthMetrics.ratingChange.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Rating</p>
                </div>
                <div>
                  <div className="flex items-center justify-center space-x-1">
                    <TrendingUp className="w-3 h-3 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      +{growthMetrics.reviewsGrowth.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Reviews</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleReviewCard;